import { Button, RadioGroup, Switch } from 'devextreme-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardFooter, Col, Row } from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import erorSound from '../../assets/audio/alert-error.wav';
import warningSound from '../../assets/audio/alert-warning.wav';
import Breadcrumb from '../../components/common/breadcrumb';
import SorgulamaEkraniV2 from '../../components/common/sorgulama-component/sorgulama-ekraniV2';
import { checkWarehouseSelection, printLabel, WAREHOUSE_PRINT_CONFIG } from '../../utils/LabelServiceV2';
import useFormatMessage from '../../utils/useFormatMessage';
import HasarBildirimiAddModal from '../hasar-bildirimi/components/HasarBildirimiAddModal';

const PDIComponentV2 = ({ title, radioItems, defaultRadio, showSwitch, api, modalArea, minCharacter, depo, role_name }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useFormatMessage();
    const [dataSource, setDataSource] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [currentChassisNo, setCurrentChassisNo] = useState("");

    // Switch state sadece PDI Çıkış için
    const [printEnabled, setPrintEnabled] = useState(true);
    const isPDIExit = title === "PDI Çıkış";

    useEffect(() => {
        // Check warehouse selection on component mount
        const checkWarehouse = async () => {
            const storedWarehouseId = localStorage.getItem("warehouse");
            const storedWarehouseName = localStorage.getItem("warehouseName");

            if (storedWarehouseId && storedWarehouseName) {
                setSelectedWarehouse({
                    id: Buffer.from(storedWarehouseId, 'base64').toString('ascii'),
                    warehouse_name: storedWarehouseName
                });
            } else if (isPDIExit) {
                const selectedWarehouse = await checkWarehouseSelection();
                if (selectedWarehouse) {
                    setSelectedWarehouse(selectedWarehouse);
                }
            }
        };

        checkWarehouse();
    }, [isPDIExit]);

    const notify = (message) => toast.error(`${message}`);
    const notifySuccess = (message) => {
        toast.success(message, {
            autoClose: 1000,
            position: "top-right",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true
        });
    };

    useEffect(() => {
        if (!dataSource) return;

        const handleResponse = async () => {
            // Hata durumu
            if (dataSource?.success === false) {
                if (navigator.vibrate) {
                    navigator.vibrate(500);
                }
                const errorSound = document.getElementById("errorSound");
                errorSound.play();

                let error = dataSource?.message ? t(dataSource?.message) : t(dataSource?.error) ? t(dataSource?.error) : t("Bir hata oluştu!");
                let adres = dataSource.data ? dataSource.data.ZoneName + " " + dataSource.data.AddressCode : null;

                Swal.fire({
                    icon: 'error',
                    title: 'Hata',
                    html: `<div style="font-size: 24px;">${error.replaceAll("chassis", "Şase").replaceAll("is in an inactive groupage", "aktif bir grupajda değil.")}</div>
                    ${adres ? ` <br/><div style="margin-top: 15px;"><strong>${adres}</strong></div>` : ''}`,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    background: '#fff5f5',
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                });
            }
            // Uyarı durumu
            else if (dataSource?.warning) {
                const warningSound = document.getElementById("warningSound");
                warningSound.play();

                await Swal.fire({
                    title: '⚠️',
                    html: `<div style="font-size: 24px;">
                        <div style="color: red !important; font-weight: 700; font-size: 2.5rem; padding: 10px; background: #ffdb9a; margin-top: 10px; border-radius: 10px;">${dataSource.warning.info}</div>
                        <div style="color:green; font-weight:700; margin-top: 10px;">
                            <span>✔</span> İşlem başarılı!
                        </div>
                        ${dataSource.address ? `
                            <div style="margin-top: 15px; color: #28a745;">
                                ${dataSource.address.address_code}
                            </div>
                        ` : ''}
                    </div>`,
                    showConfirmButton: true,
                    confirmButtonText: 'Tamam',
                    confirmButtonColor: '#ffc107',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    customClass: {
                        popup: 'swal2-popup-large'
                    }
                });

            }
            // Başarılı durumu
            else if (dataSource?.success === true) {
                if (isPDIExit && printEnabled) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                    // Run printLabel without waiting for it
                    printLabel(currentChassisNo, Buffer.from(localStorage.getItem("warehouse"), 'base64').toString('ascii'));
                }
                if (dataSource.address || dataSource?.data) {
                    let adress = typeof dataSource.address === 'string' ? dataSource.address :
                        dataSource.address?.address_code ? dataSource.address.address_code :
                            (dataSource.data ? `${dataSource.data.ZoneName} ${dataSource.data.AddressCode}` : '');
                    let groupNo = dataSource.data?.GroupageNo;

                    await Swal.fire({
                        html: `<div>
                            <div style="color: #28a745; font-size: 50px; margin-bottom: 10px;">✔</div>
                            <div style="font-size: 24px;">
                                <b>${adress}</b>
                                ${groupNo ? `<br/><span style="font-size: 20px;">Grupaj No: ${groupNo}</span>` : ''}
                            </div>
                        </div>`,
                        timer: 3000,
                        showConfirmButton: false,
                        position: 'center',
                        allowOutsideClick: false,
                        customClass: {
                            popup: 'swal2-popup-large'
                        }
                    });
                } else {
                    notifySuccess("Başarılı");
                }
            }
        };

        handleResponse();
    }, [dataSource]);

    // Handle chassis number updates
    const handleChassisUpdate = (value) => {
        setCurrentChassisNo(value);
    };

    return (
        <div>
            <audio id="errorSound" src={erorSound} preload="auto"></audio>
            <audio id="warningSound" src={warningSound} preload="auto"></audio>
            <Breadcrumb current={t(title)} depo={depo} />
            <Card>
                {/* Warehouse display for PDI Çıkış */}
                {isPDIExit && selectedWarehouse && (
                    <div style={{
                        padding: '10px 20px',
                        borderBottom: '1px solid #eee',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>
                        <small style={{
                            fontWeight: '500',
                            color: '#444',
                        }}>
                            {selectedWarehouse.warehouse_name}
                            {!WAREHOUSE_PRINT_CONFIG[selectedWarehouse.id]?.hasPrint &&
                                <span style={{ color: '#dc3545', marginLeft: '5px' }}>(Yazıcı Yok)</span>
                            }
                        </small>
                    </div>
                )}

                <Row className='m-3'>
                    <Col md="12">
                        <RadioGroup
                            id='HasarBildir'
                            items={radioItems}
                            defaultValue={defaultRadio}
                            stylingMode='outlined'
                            layout='horizontal'
                            itemRender={(data) => (
                                <div>
                                    <span>{t(data)}</span>
                                </div>
                            )}
                            onValueChange={(e) => {
                                if (e === "Hasar Bildir") {
                                    document.querySelector('.hasar-bildirimi').style.display = "block";
                                } else {
                                    document.querySelector('.hasar-bildirimi').style.display = "none";
                                }
                            }}
                        />
                    </Col>
                </Row>
                {/* Switch sadece PDI Çıkış için gösterilir */}
                {isPDIExit && showSwitch && (
                    <Row className='m-3'>
                        <Col md="12">
                            <span style={{ fontWeight: "600" }}>{t("Etiket Yazdır")}&emsp;</span>
                            <Switch
                                width={100}
                                defaultValue={true}
                                onValueChanged={(e) => setPrintEnabled(e.value)}
                            />
                        </Col>
                    </Row>
                )}
                <SorgulamaEkraniV2
                    apiEndpoint={api}
                    setData={setDataSource}
                    minCharacter={minCharacter}
                    role_name={role_name}
                    title={title}
                    onChassisUpdate={handleChassisUpdate}
                />
                <CardFooter style={{
                    textAlign: "right",
                    color: "red",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "flex-end"
                }}>
                    <Button onClick={() => {
                        if (currentChassisNo.length < 17) {
                            notify(t("17 karakterli şase numarası giriniz"))
                        }
                        else setIsOpen(!isOpen)
                    }} className='hasar-bildirimi' style={{ display: "none", width: "max-content", padding: "5px", color: "white", background: "#ff4040" }}>
                        <i>
                            {t("Hasar Bildir")}
                        </i>
                    </Button>
                </CardFooter>
            </Card>
            <HasarBildirimiAddModal isOpen={isOpen} setIsOpen={setIsOpen} area={modalArea} />

            <style>
                {`
                    .swal2-popup-large {
                        width: 600px !important;
                    }
                    .swal2-title {
                        font-size: 28px !important;
                    }
                    .swal2-html-container {
                        font-size: 24px !important;
                        margin: 1em 0 !important;
                    }
                `}
            </style>
        </div>
    );
};

export default PDIComponentV2;